import { injectable } from 'inversify';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { EditorSession } from '../../canvas/EditorSession';
import { ContentPayload } from '../../canvas/injection/ContentPayload';
import { ContentProcessor } from '../../canvas/injection/ContentProcessor';
import { DeqoData } from '../../canvas/injection/DeqoData';
import { resolve } from '../../Container';
import { PopupManager } from '../../popups/PopupManager';
import { TabularData, TabularImportParams } from './TabularImportModel';
import { tabularImportPopup } from './TabularImportPopup';

@injectable()
export class TableFromHtmlDataProcessor implements ContentProcessor {

    @resolve(CommandDispatcher) dispatch!: CommandDispatcher;
    @resolve(EditorSession) session!: EditorSession;
    @resolve(ObjectManager) objects!: ObjectManager;
    @resolve(PopupManager) popups!: PopupManager;

    public readonly supportedTypes = Object.freeze([
        'text/html',
    ]);

    public async process(payload: ContentPayload): Promise<boolean | ContentPayload> {

        // Try read text
        const textData = await payload.getDataAsText();
        if (!textData) return false;

        const mimeType = payload.type;
        const { method } = payload.metadata;
        const [tabularData, hasHeaders] = this.tryParseHtmlTable(textData);
        if (!tabularData) return false;

        const tuples = await this.invokePopup(tabularData, { hasHeaders, method, mimeType });

        if (tuples && tuples.length) {
            const ddt = DeqoData.wrap(tuples);
            return ContentPayload.derive(payload, 'x/deqo-tuple', ddt);
        }

        return Promise.resolve(true);
    }

    private async invokePopup(data: TabularData, initialParams: TabularImportParams) {
        const pr = await this.popups.invoke(tabularImportPopup(data, initialParams));
        if (!pr.data) return [];
        const { params, strategy } = pr.data;
        return strategy.apply(data, params);
    }

    private tryParseHtmlTable(source: string) {

        const parser = new DOMParser();
        const doc = parser.parseFromString(source, 'text/html');

        const table = doc.querySelector('table');

        if (table) {
            const hasHeaders = table.querySelectorAll('th').length > 0;
            const rows = Array.from(table.querySelectorAll('tr'));
            const data = rows.map(tr => Array.from(tr.querySelectorAll('th, td')).map(tx => tx.innerHTML?.trim() || ''));
            return [data as TabularData, hasHeaders] as const;
        }

        return [null, false] as const;
    }
}
